import { useMatchingTransactionsCount } from '@/hooks/use-matching-transactions-count';
import {
  rawRulesUtils,
  type KeywordMatchType,
} from '@polybank/shared/rule-utils';
import { AIRuleResponse } from '@polybank/shared/types/ai-contracts';
import { Check, Loader2, LucideIcon } from 'lucide-react';
import { motion } from 'motion/react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface MatchTypeRadioItemProps {
  matchType: KeywordMatchType;
  icon: LucideIcon;
  label: string;
  description: string;
  isSelected: boolean;
  originalRule?: AIRuleResponse;
  choosenKeyword?: string;
  onClick: () => void;
  onViewTransactions: (rawRule: AIRuleResponse) => void;
}

export function MatchTypeRadioItem({
  matchType,
  icon: Icon,
  label,
  description,
  isSelected,
  originalRule,
  onViewTransactions,
  choosenKeyword,
  onClick,
}: MatchTypeRadioItemProps) {
  const { t } = useTranslation();
  const rule = useMemo(() => {
    if (!originalRule || !choosenKeyword) return undefined;
    return rawRulesUtils.changeKeyword(originalRule, choosenKeyword, matchType);
  }, [choosenKeyword, originalRule, matchType]);

  const { count: matchingCount, isLoading: isLoadingMatchCount } =
    useMatchingTransactionsCount(rule);
  return (
    <div
      onClick={onClick}
      className={`relative cursor-pointer border-2 rounded-xl p-4 transition-all ${
        isSelected
          ? 'border-purple-500 bg-purple-50/50 dark:bg-purple-950/20 shadow-sm'
          : 'border-border hover:border-muted-foreground/50 bg-background'
      }`}
    >
      {/* Checkmark in top-left corner */}
      {isSelected && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className='absolute -left-2 -top-2 w-6 h-6 rounded-full bg-purple-500 flex items-center justify-center shadow-md'
        >
          <Check className='w-4 h-4 text-white' strokeWidth={3} />
        </motion.div>
      )}

      <div className='flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4'>
        <div className='flex items-start gap-3 flex-1 min-w-0'>
          <div className='mt-0.5 flex-shrink-0'>
            <Icon className='w-5 h-5' />
          </div>
          <div className='flex-1 min-w-0'>
            <div className='font-semibold'>{label}</div>
            <p className='text-sm text-muted-foreground leading-relaxed'>
              {description}
            </p>
          </div>
        </div>
        {isLoadingMatchCount ? (
          <div className='flex items-center gap-2 sm:flex-col sm:items-end sm:gap-0 pl-8 sm:pl-0 flex-shrink-0'>
            <div className='font-semibold text-sm sm:text-base whitespace-nowrap'>
              <Loader2 className='w-4 h-4 animate-spin' />
            </div>
          </div>
        ) : (
          <div className='flex items-center gap-2 sm:flex-col sm:items-end sm:gap-0 pl-8 sm:pl-0 flex-shrink-0'>
            <div className='font-semibold text-sm sm:text-base whitespace-nowrap'>
              {matchingCount}{' '}
              {matchingCount > 1
                ? t('aiAutomation.matches')
                : t('aiAutomation.match')}
            </div>
            <button
              className='text-xs text-purple-600 dark:text-purple-400 hover:underline font-medium'
              onClick={() => !!rule && onViewTransactions(rule)}
            >
              {t('aiAutomation.show')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
